import { IconButton, Popover } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Button } from "js/Components/Button";
import { Icon } from "js/Components/Icon";
import BillingController from "js/controllers/BillingController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import { app } from "js/namespaces";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import { useScreenSize } from "js/react/hooks/useScreenSize";
import { InviteUsersDialog } from "js/react/views/UserOptions/panes/ManageUsersPane/Components/InviteUsersDialog";

const STORAGE_KEY_SHOWN_PREFIX = "invite-member-coach-mark-shown:";
const POPOVER_WIDTH_PX = 294;
const POPOVER_GAP_PX = 12;

const CloseButton = styled(IconButton)`
    &&& {
        position: absolute;
        top: 16px;
        right: 16px;
    }
`;

const Title = styled.div`
    font-family: "Source Sans Pro";
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%; /* 21.6px */
    letter-spacing: 0.5px;
    color: #000;
`;

const Body = styled.div`
    color: #000;
    font-family: "Source Sans Pro";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 125%; /* 20px */
    letter-spacing: -0.32px;

    span {
        font-weight: 700;
    }
`;

const StyledPopover = styled(Popover)`
    &&& {
        pointer-events: none;

        .MuiPaper-root {
            pointer-events: auto;
        }
    
    }
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const TextContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const StyledButton = styled(Button)`
    width: fit-content;
    align-self: self-end;
    padding: 6px 16px;
`;

function getAnchorPosition() {
    const rect = document.querySelector("#user-menu-trigger")?.getBoundingClientRect?.();
    if (!rect) {
        return null;
    }
    return { top: rect.top, left: rect.left - POPOVER_WIDTH_PX - POPOVER_GAP_PX };
}

export function setInviteMembersCoachMarkShown(workspaceId: string) {
    localStorage.setItem(`${STORAGE_KEY_SHOWN_PREFIX}${workspaceId}`, "true");
}

function InviteMembersCoachMark({ workspace }: WorkspaceControllerState) {
    const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null);
    const { isMobile } = useScreenSize();

    const hasAvailableSeats = BillingController.availableSeatCount > 0;

    useEffect(() => {
        if (
            isMobile ||
            !hasAvailableSeats ||
            app.dialogManager?.openDialogs?.length
        ) {
            setAnchorPosition(null);
            return;
        }

        const storageKey = `${STORAGE_KEY_SHOWN_PREFIX}${workspace.id}`;

        if (localStorage.getItem(storageKey) === "true") {
            setAnchorPosition(null);
            return;
        }

        setAnchorPosition(getAnchorPosition());
        localStorage.setItem(storageKey, "true");
    }, [workspace.id, hasAvailableSeats, isMobile]);

    const handleClose = () => {
        setAnchorPosition(null);
    };

    if (!anchorPosition) {
        return null;
    }

    return (
        <StyledPopover
            open={!!anchorPosition}
            onClose={handleClose}
            anchorReference="anchorPosition"
            anchorPosition={anchorPosition}
            transitionDuration={0}
            PaperProps={{
                style: {
                    width: POPOVER_WIDTH_PX,
                    background: "#FFFFFF",
                    borderRadius: 8,
                    boxShadow: "0 9px 37px 0 rgba(0, 0, 0, 0.20)",
                    padding: 16,
                    position: "relative",
                    zIndex: 10
                }
            }}
            disableRestoreFocus
            disableEnforceFocus
            disableAutoFocus
            disableBackdropClick
        >
            <CloseButton onClick={handleClose} size="small">
                <Icon color="#111" large>close</Icon>
            </CloseButton>
            <ContentContainer>
                <TextContainer>
                    <Title>Invite team members</Title>
                    <Body>
                        Invite your teammates now—or later from <span>Manage Account</span> → <span>People</span>.
                    </Body>
                </TextContainer>

                <StyledButton
                    blue
                    large
                    onClick={() => {
                        handleClose();
                        AppController.showAccountPane({ pane: "manage-workspace-members" });
                        // Add a small delay to make sure the pane is loaded
                        // and the dialog is shown
                        setTimeout(() => {
                            ShowDialog(InviteUsersDialog);
                        }, 500);
                    }}
                >
                    Invite members
                </StyledButton>
            </ContentContainer>
        </StyledPopover>
    );
}

export default WorkspaceController.withInitializedState(BillingController.withInitializedState(InviteMembersCoachMark));
